import create from 'zustand/vanilla';
import { CarouselStyle, CutType, GenerateResult, ImageRole, Ratio } from '@/services/carousel.service';

/**
 * AI 캐러셀 생성 플로우 상태.
 * 컷 → 디자인 → 상품정보/사진/옵션 → 생성 단계를 가로질러 입력값을 보관한다.
 * payload 는 컷마다 구조가 달라 컷 변경 시 기본값으로 초기화한다.
 */

/** 컷별 payload 초기값 */
export const defaultPayload = (cutType: CutType): Record<string, any> => {
    switch (cutType) {
        case 'cover':
            return { main: '', sub: '', period: '' };
        case 'price':
            return { options: [{ name: '', originalPrice: '', price: '' }], highlight: '', extras: [] };
        case 'info':
            return { body: '', pharmComment: '' };
        case 'graph':
            return {
                graphType: 'barH',
                itemLabel: '',
                unit: '',
                data: [{ name: '', value: 0 }],
                highlight: '',
                description: '',
            };
        case 'qna':
            return { qnaList: [{ question: '', answer: '' }] };
        default:
            return {};
    }
};

export interface CarouselImageSlot {
    url: string;
}

/** 생성된 캐러셀 한 장(슬라이드). 여러 장을 모아 하나의 캐러셀 세트가 된다. */
export interface CarouselSlide {
    id: string;
    cutType: CutType;
    productName: string;
    imageUrl: string;
    ment?: string;
}

/** 진행 중인 생성 작업(백그라운드). 동기 응답이라 시간 기반으로 진행 표시. */
export interface GenJob {
    id: string;
    cutType: CutType;
    productName: string;
    startedAt: number;
}

interface CarouselState {
    cutType: CutType;
    styleFilter: CarouselStyle | null;
    selectedDesignId: string | null;
    productName: string;
    payload: Record<string, any>;
    images: Partial<Record<ImageRole, CarouselImageSlot>>;
    uploadingRole: ImageRole | null;
    pointColor: string;
    accentColor: string;
    ratio: Ratio;
    pharmacyName: string;
    autoMent: boolean;
    /** 진행 중인 생성 작업 목록 (백그라운드, 동시 다발 가능) */
    jobs: GenJob[];
    result: GenerateResult | null;
    /** 이번 세션에 모은 캐러셀 세트(슬라이드 순서대로) */
    set: CarouselSlide[];
}

interface CarouselOptionPatch {
    pointColor?: string;
    accentColor?: string;
    ratio?: Ratio;
    pharmacyName?: string;
    autoMent?: boolean;
}

export interface CarouselStore extends CarouselState {
    setCutType: (cutType: CutType) => void;
    setStyleFilter: (style: CarouselStyle | null) => void;
    setSelectedDesignId: (id: string | null) => void;
    setProductName: (name: string) => void;
    patchPayload: (patch: Record<string, any>) => void;
    setImage: (role: ImageRole, url: string) => void;
    removeImage: (role: ImageRole) => void;
    setUploadingRole: (role: ImageRole | null) => void;
    setOption: (patch: CarouselOptionPatch) => void;
    addJob: (job: GenJob) => void;
    removeJob: (id: string) => void;
    setResult: (result: GenerateResult | null) => void;
    addToSet: (slide: CarouselSlide) => void;
    removeFromSet: (id: string) => void;
    moveSlide: (id: string, dir: -1 | 1) => void;
    /** 드래그앤드롭 재정렬 — activeId 를 overId 위치로 이동 */
    reorderSet: (activeId: string, overId: string) => void;
    clearSet: () => void;
    /** 공통 컨텍스트(상품명·색상·사진 등) 유지하고 다음 컷 입력 준비 */
    continueNextCut: () => void;
    reset: () => void;
}

const initialState: CarouselState = {
    cutType: 'cover',
    styleFilter: null,
    selectedDesignId: null,
    productName: '',
    payload: defaultPayload('cover'),
    images: {},
    uploadingRole: null,
    pointColor: '',
    accentColor: '',
    ratio: '3:4',
    pharmacyName: '',
    autoMent: false,
    jobs: [],
    result: null,
    set: [],
};

export const carouselStore = create<CarouselStore>((set) => ({
    ...initialState,

    setCutType: (cutType) =>
        set({
            cutType,
            // 컷이 바뀌면 스타일/선택/payload 초기화 (디자인 목록은 react-query 가 컷별로 관리)
            styleFilter: null,
            selectedDesignId: null,
            payload: defaultPayload(cutType),
            result: null,
        }),
    setStyleFilter: (styleFilter) => set({ styleFilter }),
    setSelectedDesignId: (selectedDesignId) => set({ selectedDesignId }),
    setProductName: (productName) => set({ productName }),
    patchPayload: (patch) => set((s) => ({ payload: { ...s.payload, ...patch } })),
    setImage: (role, url) => set((s) => ({ images: { ...s.images, [role]: { url } } })),
    removeImage: (role) =>
        set((s) => {
            const next = { ...s.images };
            delete next[role];
            return { images: next };
        }),
    setUploadingRole: (uploadingRole) => set({ uploadingRole }),
    setOption: (patch) => set(patch),
    addJob: (job) => set((s) => ({ jobs: [...s.jobs, job] })),
    removeJob: (id) => set((s) => ({ jobs: s.jobs.filter((j) => j.id !== id) })),
    setResult: (result) => set({ result }),
    addToSet: (slide) =>
        set((s) => (s.set.some((x) => x.id === slide.id) ? {} : { set: [...s.set, slide] })),
    removeFromSet: (id) => set((s) => ({ set: s.set.filter((x) => x.id !== id) })),
    moveSlide: (id, dir) =>
        set((s) => {
            const idx = s.set.findIndex((x) => x.id === id);
            const ni = idx + dir;
            if (idx < 0 || ni < 0 || ni >= s.set.length) return {};
            const next = [...s.set];
            [next[idx], next[ni]] = [next[ni], next[idx]];
            return { set: next };
        }),
    reorderSet: (activeId, overId) =>
        set((s) => {
            const from = s.set.findIndex((x) => x.id === activeId);
            const to = s.set.findIndex((x) => x.id === overId);
            if (from < 0 || to < 0 || from === to) return {};
            const next = [...s.set];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            return { set: next };
        }),
    clearSet: () => set({ set: [] }),
    continueNextCut: () =>
        set((s) => ({
            selectedDesignId: null,
            payload: defaultPayload(s.cutType),
            result: null,
        })),
    reset: () => set({ ...initialState, payload: defaultPayload('cover'), images: {}, set: [] }),
}));

export default carouselStore;
